import { authService } from '../../services/auth';
import { adminApi, type UpdateBillingPlanConfigurationRequest } from '../../services/admin.api';
import { type BillingPlanDto } from '../../services/billing.api';
import { onLocaleChange, t } from '../../services/i18n';
import { esc, escAttr, formatCurrencyFromCents } from './admin-shared';

type SaveState = {
  saving: boolean;
  error: string;
};

export class RcAdminBillingPlans extends HTMLElement {
  private static readonly vatRate = 0.21;
  private plans: BillingPlanDto[] = [];
  private isLoading = false;
  private error = '';
  private saveStates = new Map<string, SaveState>();
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    this.render();
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    if (authService.isAuthenticated && authService.isAdmin) {
      void this.loadPlans();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
  }

  private async loadPlans(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.render();

    try {
      this.plans = await adminApi.getBillingPlans();
    } catch (error: any) {
      this.error = error?.message || t('admin.billingPlans.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    if (!authService.isAuthenticated || !authService.isAdmin) {
      this.innerHTML = `<div class="empty-state">${t('admin.accessDenied')}</div>`;
      return;
    }

    if (this.isLoading) {
      this.innerHTML = `<div class="empty-state">${t('common.loading')}</div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `<div class="empty-state">${esc(this.error)}</div>`;
      return;
    }

    this.innerHTML = `
      <div class="card">
        <div class="page-header">
          <div>
            <h2>${t('admin.billingPlans.title')}</h2>
            <p class="page-subtitle">${t('admin.billingPlans.subtitle')}</p>
          </div>
        </div>
        <div class="admin-plan-grid">
          ${this.plans.map((plan) => this.renderPlanCard(plan)).join('')}
        </div>
      </div>
    `;

    this.querySelectorAll<HTMLFormElement>('.admin-plan-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        await this.handleSubmit(form);
      });

      form.querySelectorAll<HTMLInputElement>('input[name="monthlyPrice"], input[name="yearlyPrice"]').forEach((input) => {
        input.addEventListener('input', () => this.updateVatHint(form, input.name === 'monthlyPrice' ? 'monthly' : 'yearly'));
      });

      this.updateVatHint(form, 'monthly');
      this.updateVatHint(form, 'yearly');
    });
  }

  private renderPlanCard(plan: BillingPlanDto): string {
    const state = this.saveStates.get(plan.key) ?? { saving: false, error: '' };

    return `
      <form class="admin-plan-form" data-plan-key="${escAttr(plan.key)}">
        <div class="admin-users-toolbar">
          <div class="admin-users-toolbar-main">
            <strong>${esc(plan.displayName)}</strong>
            <div class="admin-users-meta">${esc(plan.key)}</div>
          </div>
          <div class="admin-users-toolbar-actions">
            <button type="submit" class="btn btn-primary" ${state.saving ? 'disabled' : ''}>
              ${state.saving ? t('common.loading') : t('admin.billingPlans.save')}
            </button>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="plan-monthly-${escAttr(plan.key)}">${t('admin.billingPlans.monthlyPrice')}</label>
            <input id="plan-monthly-${escAttr(plan.key)}" name="monthlyPrice" type="number" min="0" step="0.01" value="${escAttr(this.centsToInputValue(plan.monthlyPriceInCents))}" />
            <div class="admin-users-meta" data-vat-hint="monthly">${esc(this.getVatHint(plan.monthlyPriceInCents ?? 0))}</div>
          </div>
          <div class="form-group">
            <label for="plan-yearly-${escAttr(plan.key)}">${t('admin.billingPlans.yearlyPrice')}</label>
            <input id="plan-yearly-${escAttr(plan.key)}" name="yearlyPrice" type="number" min="0" step="0.01" value="${escAttr(this.centsToInputValue(plan.yearlyPriceInCents))}" />
            <div class="admin-users-meta" data-vat-hint="yearly">${esc(this.getVatHint(plan.yearlyPriceInCents ?? 0))}</div>
          </div>
          <div class="form-group">
            <label for="plan-properties-${escAttr(plan.key)}">${t('admin.billingPlans.propertyLimit')}</label>
            <input id="plan-properties-${escAttr(plan.key)}" name="propertyLimit" type="number" min="1" step="1" value="${escAttr(String(plan.propertyLimit))}" />
          </div>
          <div class="form-group">
            <label for="plan-storage-${escAttr(plan.key)}">${t('admin.billingPlans.storageLimitGb')}</label>
            <input id="plan-storage-${escAttr(plan.key)}" name="storageLimitGb" type="number" min="0.1" step="0.1" value="${escAttr(this.bytesToGbInputValue(plan.storageLimitBytes))}" />
          </div>
        </div>
        ${state.error ? `<div class="form-error">${esc(state.error)}</div>` : ''}
      </form>
    `;
  }

  private async handleSubmit(form: HTMLFormElement): Promise<void> {
    const planKey = form.dataset.planKey;
    if (!planKey) {
      return;
    }

    const currentPlan = this.plans.find((plan) => plan.key === planKey);
    if (!currentPlan) {
      return;
    }

    const formData = new FormData(form);
    const requestBody = this.buildRequestBody(formData);
    this.saveStates.set(planKey, { saving: true, error: '' });
    this.render();

    try {
      const updatedPlan = await adminApi.updateBillingPlan(planKey, requestBody);
      this.plans = this.plans.map((plan) => plan.key === planKey ? updatedPlan : plan);
      this.saveStates.set(planKey, { saving: false, error: '' });
    } catch (error: any) {
      this.saveStates.set(planKey, { saving: false, error: error?.message || t('admin.billingPlans.saveError') });
    }

    this.render();
  }

  private buildRequestBody(formData: FormData): UpdateBillingPlanConfigurationRequest {
    const monthlyPrice = Number(String(formData.get('monthlyPrice') || '0'));
    const yearlyPrice = Number(String(formData.get('yearlyPrice') || '0'));
    const propertyLimit = Number(String(formData.get('propertyLimit') || '0'));
    const storageLimitGb = Number(String(formData.get('storageLimitGb') || '0'));

    return {
      monthlyPriceInCents: Math.round(monthlyPrice * 100),
      yearlyPriceInCents: Math.round(yearlyPrice * 100),
      propertyLimit,
      storageLimitBytes: Math.round(storageLimitGb * 1024 * 1024 * 1024),
    };
  }

  private centsToInputValue(value?: number): string {
    return typeof value === 'number' ? (value / 100).toFixed(2) : '0.00';
  }

  private getVatHint(grossPriceInCents: number): string {
    const priceExcludingVatInCents = Math.round(grossPriceInCents / (1 + RcAdminBillingPlans.vatRate));
    return t('admin.billingPlans.priceExcludingVat', {
      amount: formatCurrencyFromCents(priceExcludingVatInCents),
    });
  }

  private updateVatHint(form: HTMLFormElement, interval: 'monthly' | 'yearly'): void {
    const inputName = interval === 'monthly' ? 'monthlyPrice' : 'yearlyPrice';
    const input = form.querySelector<HTMLInputElement>(`input[name="${inputName}"]`);
    const hint = form.querySelector<HTMLElement>(`[data-vat-hint="${interval}"]`);
    if (!input || !hint) {
      return;
    }

    const grossPrice = Number(input.value || '0');
    const grossPriceInCents = Number.isFinite(grossPrice) ? Math.max(0, Math.round(grossPrice * 100)) : 0;
    hint.textContent = this.getVatHint(grossPriceInCents);
  }

  private bytesToGbInputValue(value: number): string {
    return (value / (1024 * 1024 * 1024)).toFixed(1);
  }
}

if (!customElements.get('rc-admin-billing-plans')) {
  customElements.define('rc-admin-billing-plans', RcAdminBillingPlans);
}
